import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, FormControl, InputLabel, Select, MenuItem, TextField, Button, InputAdornment, CircularProgress } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import { getTransactions, exportTransactions } from '../../api/endpoints/wallet';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      const res = await getTransactions(params);
      setTransactions(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      // toast handled by axiosClient
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, typeFilter, categoryFilter]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleExportCSV = async () => {
    try {
      const res = await exportTransactions({ page: 1, limit: 10000 });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (err) {
      toast.error('Failed to export transactions');
    }
  };

  const columns = [
    { id: '_id', label: 'ID', render: (row) => <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{(row._id || '').slice(-8)}</span> },
    { id: 'user', label: 'User', render: (row) => row.userId?.name || row.user?.name || '-' },
    { id: 'type', label: 'Type', render: (row) => <StatusChip status={row.type === 'credit' ? 'completed' : 'failed'} label={row.type} /> },
    { id: 'category', label: 'Category' },
    { id: 'amount', label: 'Amount', align: 'right', render: (row) => formatCurrency(row.amount) },
    { id: 'description', label: 'Description', render: (row) => row.description || '-' },
    { id: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
    { id: 'createdAt', label: 'Date', render: (row) => formatDateTime(row.createdAt) },
  ];

  return (
    <Box>
      <PageHeader title="Transactions" subtitle="View all wallet transactions">
        <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportCSV}>
          Export CSV
        </Button>
      </PageHeader>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by ID or user..."
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="credit">Credit</MenuItem>
            <MenuItem value="debit">Debit</MenuItem>
            <MenuItem value="refund">Refund</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Category</InputLabel>
          <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="chat">Chat</MenuItem>
            <MenuItem value="call">Call</MenuItem>
            <MenuItem value="recharge">Recharge</MenuItem>
            <MenuItem value="order">Order</MenuItem>
            <MenuItem value="withdrawal">Withdrawal</MenuItem>
            <MenuItem value="course">Course</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <DataTable
        columns={columns}
        rows={transactions}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        loading={loading}
      />
    </Box>
  );
};

export default Transactions;